/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import React from 'react'
import { Link } from 'react-router-dom'
import colors from '@sportninja/common/constants/appColors'
import Text from '../ts-components/Text'
import VerifiedIcon from './VerifiedIcon'
import {
  profileLinkStyles,
  avatarContainerStyles,
  profileInfoStyles,
  getAvatarStyles,
  getUserProfileStyles,
} from './FeedItem.styles'

/**
 * Returns background and foreground colors for a given role id
 * using PRIMARY, INFO, and NEUTRAL palettes to avoid confusion.
 */
const getRoleColors = (roleId: number) => {
  // High-privilege/admin roles → PRIMARY
  if (roleId === 0 || roleId === 1 || roleId === 2) {
    return { bg: colors.PRIMARY_50, fg: colors.PRIMARY_400 }
  }

  // Officiating/timekeeping roles → INFO
  if (roleId === 3 || roleId === 4 || roleId === 8) {
    return { bg: colors.INFO_50, fg: colors.INFO_400 }
  }

  // General participants/others → NEUTRAL
  return { bg: colors.NEUTRAL_20, fg: colors.NEUTRAL_500 }
}

/**
 * Props for the ProfileLink component
 */
interface ProfileLinkProps {
  entityType: 'team' | 'schedule' | 'organization'
  isAppPost: boolean
  avatarSrc: string
  displayName: string
  formattedDate: string
  isOrgOrSchedulePost: boolean
  userProfileSrc: string
  entityName: string
  dateText: string
  navigationUrl?: string
  navigationCursor: string
  handleNavigationClick: (e: React.MouseEvent) => void
  role_types?: {
    id: number
    name: string
  }[]
}

/**
 * ProfileLink component for displaying entity/user profile information
 * @param props - Component props
 */
const ProfileLink: React.FC<ProfileLinkProps> = ({
  isAppPost,
  avatarSrc,
  displayName,
  formattedDate,
  isOrgOrSchedulePost,
  userProfileSrc,
  entityName,
  dateText,
  navigationUrl,
  navigationCursor,
  handleNavigationClick,
  role_types = [],
}) => {
  const linkContent = isOrgOrSchedulePost ? (
    <>
      <img src={avatarSrc} alt='Entity logo' css={getAvatarStyles()} />
      <div>
        <div css={profileInfoStyles}>
          <Text variant='paragraphMedium' weight='bold' color={colors.WHITE}>
            {displayName}
          </Text>
          {isAppPost && <VerifiedIcon />}
        </div>
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 4px;
            margin-top: 2px;
          `}
        >
          <Text
            variant='paragraphXSmall'
            weight='regular'
            color={colors.NEUTRAL_50}
          >
            {formattedDate}
          </Text>
          {role_types && role_types.length > 0 && (
            <div
              css={css`
                display: flex;
                align-items: center;
                flex-wrap: wrap;
                gap: 4px;
                max-width: 100%;
                display: none;
              `}
            >
              <Text
                variant='paragraphXSmall'
                weight='regular'
                color={colors.NEUTRAL_50}
                style='white-space: nowrap;'
              >
                Visible to:
              </Text>
              {role_types.map((role, index) => (
                <span
                  key={index}
                  css={css`
                    background: ${getRoleColors(role.id).bg};
                    color: ${getRoleColors(role.id).fg};
                    padding: 0px 4px;
                    border-radius: 4px;
                    font-size: 12px;
                    line-height: 18px;
                    font-weight: 500;
                    white-space: nowrap;
                    flex-shrink: 0;
                  `}
                >
                  {role.name}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  ) : (
    <>
      <div css={avatarContainerStyles}>
        <img src={avatarSrc} alt='Entity logo' css={getAvatarStyles()} />
        <img
          src={userProfileSrc}
          alt='User profile picture'
          css={getUserProfileStyles}
        />
      </div>
      <div>
        <div css={profileInfoStyles}>
          <Text variant='paragraphMedium' weight='bold' color={colors.WHITE}>
            {entityName}
          </Text>
        </div>
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 4px;
          `}
        >
          <Text
            variant='paragraphXSmall'
            weight='regular'
            color={colors.NEUTRAL_50}
          >
            {dateText}
          </Text>
          {role_types && role_types.length > 0 && (
            <div
              css={css`
                display: flex;
                align-items: center;
                flex-wrap: wrap;
                gap: 4px;
                max-width: 100%;
                display: none;
              `}
            >
              <Text
                variant='paragraphXSmall'
                weight='regular'
                color={colors.NEUTRAL_50}
                style='white-space: nowrap;'
              >
                Visible to:
              </Text>
              {role_types.map((role, index) => (
                <span
                  key={index}
                  css={css`
                    background: ${getRoleColors(role.id).bg};
                    color: ${getRoleColors(role.id).fg};
                    padding: 0px 4px;
                    border-radius: 4px;
                    font-size: 12px;
                    line-height: 18px;
                    font-weight: 500;
                    white-space: nowrap;
                    flex-shrink: 0;
                  `}
                >
                  {role.name}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  )

  if (navigationUrl) {
    return (
      <Link
        to={navigationUrl}
        css={[
          profileLinkStyles,
          css`
            cursor: ${navigationCursor};
          `,
        ]}
        onClick={handleNavigationClick}
      >
        {linkContent}
      </Link>
    )
  }

  return (
    <span
      css={[
        profileLinkStyles,
        css`
          cursor: ${navigationCursor};
        `,
      ]}
      onClick={handleNavigationClick}
    >
      {linkContent}
    </span>
  )
}

export default ProfileLink
